import { createColumnHelper } from "@tanstack/react-table";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Icon } from "semantic-ui-react";

import { Can, ReactTable } from "components";
import "modules/NoteSuggestions/components/NoteSuggestionsTable/NoteSuggestionsTable.scss";
import { useNoteSuggestionMutation } from "modules/NoteSuggestions/hooks";

import type { NOTE_SUGGESTION_TYPES } from "models";
import type { NoteSuggestionTypeWithSuggestions } from "modules/NoteSuggestions/hooks";

type NoteSuggestionsTableProps = {
  noteTypesWithSuggestions: NoteSuggestionTypeWithSuggestions[];
  openModal: (id: NOTE_SUGGESTION_TYPES) => void;
};

const toggleNoteTypeVisibility = (noteTypeWithSuggestion: NoteSuggestionTypeWithSuggestions) => {
  return {
    ...noteTypeWithSuggestion,
    note_suggestion_type: { ...noteTypeWithSuggestion.note_suggestion_type, active: !noteTypeWithSuggestion.note_suggestion_type.active }
  };
};

const columnHelper = createColumnHelper<NoteSuggestionTypeWithSuggestions>();

export const NoteSuggestionsTable = ({ openModal, noteTypesWithSuggestions }: NoteSuggestionsTableProps) => {
  const { t } = useTranslation();

  const { mutate } = useNoteSuggestionMutation();

  const columns = useMemo(
    () => [
      columnHelper.accessor((row: NoteSuggestionTypeWithSuggestions) => row, {
        id: "note_suggestions",
        header: () => <span>{t("v8_note_type", "Note Type")}</span>,
        size: 800,
        cell: ({ row: { original: noteTypeWithSuggestion } }) => {
          const visibilityIcon = `eye ${noteTypeWithSuggestion.note_suggestion_type.active ? "green-lime" : "slash red"}`;
          return (
            <div className="note-info-container">
              <Icon onClick={() => mutate(toggleNoteTypeVisibility(noteTypeWithSuggestion))} className={`${visibilityIcon} visibilityToggle`} />
              <Icon className={`${noteTypeWithSuggestion.icon} green-lime`} />
              <span>{t(noteTypeWithSuggestion.translation_key) || noteTypeWithSuggestion.default_message}</span>
            </div>
          );
        }
      }),
      columnHelper.accessor((row: NoteSuggestionTypeWithSuggestions) => row, {
        id: "note_interactions",
        size: 10,
        header: () => <span></span>,
        cell: ({
          row: {
            original: {
              note_suggestion_type: { note_suggestion_type_id }
            }
          }
        }) => (
          <Can I="update" the="note-suggestions">
            <div className="edit-button-wrapper">
              <button className="edit-button-container" onClick={() => openModal(note_suggestion_type_id)}>
                <div className="edit-button">
                  <Icon className="pen to square edit-icon" />
                </div>
              </button>
            </div>
          </Can>
        )
      })
    ],
    [t, openModal, mutate, toggleNoteTypeVisibility]
  );

  return (
    <div className="note-suggestions-container">
      <ReactTable columns={columns} data={noteTypesWithSuggestions} />
    </div>
  );
};
